import { FunctionComponent, memo, ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import { defaultViewportDimensions } from '@models/ImageComposer'
import { Pages } from '@models/Pages'
import { SurveyContext } from '@context/SurveyContext'
import { SurveyMode } from '@reducers/SurveyReducer'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import ImageComposer from '@components/ImageComposer/ImageComposer'
import InfoIcon from '@assets/images/icons/info-white.png'
import Speech from '@components/Speech/Speech'
import Widget from '@components/Widget/Widget'
import './PortraitWidget.scss'
import { FlatQuestion, TacticalQuestion } from '@models/Survey/Survey'
import { questionGroupStem } from '@utils/showIf'
import {
  categoryHasMultipleSurveysFromSurvey,
  categorySurveyDisplayName,
  getCategory,
  isLastSurveyInCategory,
  resolveResultVariant,
  sortedCategorySurveys,
  stripEditionSurveyLabel,
} from '@utils/categoryFlow'

export interface OwnProps {
  hidden?: boolean
  currentQuestion?: FlatQuestion
}

type Props = OwnProps

const PortraitWidget: FunctionComponent<Props> = ({ currentQuestion, hidden }): ReactElement => {
  const { mode, step, questionsList, survey, resultVariant } = useContext(SurveyContext)
  const { t, i18n } = useTranslation('Common')
  const { t: tSim } = useTranslation('Simulation')
  const isSandbox = [SurveyMode.SANDBOX, SurveyMode.SANDBOX_PORTRAIT].includes(mode)

  const location = useLocation()
  const answerUrl = `/${Pages.Survey}/${SurveyStep.Answer}`
  const isBubbleDisplayed = location.pathname.endsWith(answerUrl) && currentQuestion?.text
  const stem = currentQuestion
    ? questionGroupStem(currentQuestion, i18n.language, questionsList)
    : undefined

  const isTacticalQuestion = !!(currentQuestion as TacticalQuestion)?.meta?.tacticalReference
  const isNotInPrediction = !!currentQuestion?.notInResults && currentQuestion?.notInResults
  const animateGraphics = ![SurveyStep.Email, SurveyStep.Acknowledge].includes(step)
  const [isFirstLoad, setIsFirstLoad] = useState(true)

  useEffect(() => {
    setIsFirstLoad(true)
  }, [survey?.key])

  const sandboxStageNotice = useMemo(() => {
    if (!isSandbox || !categoryHasMultipleSurveysFromSurvey(survey)) {
      return null
    }
    const category = getCategory(survey)
    const lang = (i18n.language ?? 'fr').substring(0, 2)
    const refs = sortedCategorySurveys(category)
    const ref = refs.find((s) => s.id === survey?.id || s.key === survey?.key)
    const idx = ref ? refs.indexOf(ref) : Math.max(0, refs.findIndex((s) => s.key === survey?.key))
    const rawName =
      (ref && categorySurveyDisplayName(ref, lang)) ||
      tSim('sandbox.questionnaireFallback', { index: (idx >= 0 ? idx : 0) + 1 })
    const name = stripEditionSurveyLabel(rawName, category)
    const variant = resolveResultVariant(resultVariant, survey)
    const isFinal = variant === 'final' || isLastSurveyInCategory(survey)
    return {
      text: isFinal
        ? tSim('sandbox.noticePrediction', { name })
        : tSim('sandbox.noticeIntermediate', { name }),
      tone: isFinal ? 'final' : 'intermediate',
    }
  }, [isSandbox, survey, resultVariant, i18n.language, tSim])

  return (
    <Widget propClassName={`PortraitWidget ${hidden ? 'hidden' : ''}`}>
      <div className="image-composer" style={{ height: '100%' }}>
          <ImageComposer
            key={survey?.key ?? 'portrait'}
            loading={isFirstLoad}
            finishLoading={() => setIsFirstLoad(false)}
            animate={animateGraphics}
            width={defaultViewportDimensions.width}
            height={defaultViewportDimensions.height}
            currentQuestion={currentQuestion}
          />
      </div>
      {isBubbleDisplayed && !isFirstLoad && (
        <div className="PortraitWidget-Speech">
          <Speech>
            {stem ? <span className="PortraitWidget-Stem">{stem}</span> : null}
            {currentQuestion?.text}
          </Speech>
        </div>
      )}

      {!isSandbox && (isTacticalQuestion || isNotInPrediction) && isBubbleDisplayed && (
        <div className="tactical-widget">
          <img src={InfoIcon} /> {t('isTactical')}
        </div>
      )}

      {sandboxStageNotice && (
        <div
          className={[
            'tactical-widget',
            'sandbox-stage-notice',
            `sandbox-stage-notice--${sandboxStageNotice.tone}`,
          ].join(' ')}
        >
          <img src={InfoIcon} alt="" /> {sandboxStageNotice.text}
        </div>
      )}
    </Widget>
  )
}

export default memo(PortraitWidget)
